import lodash from 'lodash';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { getDimensionData } from '@api/emp/query';
import { sortByType } from '@src/common/constant/enum';
import { DimensionQueryBody } from '@src/common/types/empTypes';
import { removeEmptyFilters } from '@src/emp-3.0/utils';
import commonSelector from '@store/common/selectors';
import METRIC from '@utils/metricKeys';

const useDebounce = (value, delay) => {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      if (!delay) {
        return;
      }
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay], // Only re-call effect if value or delay changes
  );

  if (!delay) {
    return value;
  }

  return debouncedValue;
};

type TUseQueryDimensionDataResult = ({
  app_total_user_events: number;
} & {
  [key: string]: string;
})[];

export const useQueryDimensionData = (payload, debounceTime?: number) => {
  const common = useSelector(commonSelector);
  // fix HP-8823, refreshes the path page, sending a request with interval: '/'.
  // interval: {from: '', to: ''}
  const isIntervalValid = !!common?.interval?.from && !!common?.interval?.to;

  const queryBody: DimensionQueryBody = {
    queries: [
      {
        fields: [METRIC.AppTotalUserEvents],
        interval: `${common?.interval?.from}/${common?.interval?.to}`,
        filters: common?.appliedFilters?.[0]?.rule ?? [],
        limit: 500,
        sortBy: { field: [METRIC.AppTotalUserEvents], order: sortByType.DESC },
      },
    ],
  };
  const query = lodash.cloneDeep(queryBody);
  if (payload.fields?.length) {
    query.queries[0].fields = [...query.queries[0].fields, ...payload.fields].filter(Boolean);
  }
  if (payload.interval) {
    query.queries[0].interval = payload.interval;
  }
  if (payload.filters) {
    payload.filters = removeEmptyFilters(payload.filters);
    query.queries[0].filters = [...payload.filters];
  }
  const { fields, interval, sortBy, filters } = query.queries[0];
  const queryKey = `${JSON.stringify(fields)}${interval}${JSON.stringify(sortBy)}${JSON.stringify(
    filters,
  )}`;
  const queryClient = useQueryClient();
  // Debounce here for customer input something.
  const debounceQueryKey = useDebounce(queryKey, debounceTime);
  const data = queryClient.getQueryData([debounceQueryKey]);

  const queryKeyStr = data ? debounceQueryKey : queryKey;
  return useQuery<TUseQueryDimensionDataResult, any>({
    queryKey: [queryKeyStr],
    queryFn: async () => {
      const res = await getDimensionData(payload?.params, query);
      return res?.data?.data?.[0] || [];
    },
    ...payload.options,
    enabled: isIntervalValid && payload?.options?.enabled,
  });
};
